import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Grid,
  Container,
  Button,
  Avatar,
  IconButton,
  Skeleton,
  Chip,
  TextField,
  InputAdornment,
  Pagination,
  Stack
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { httpClient } from "../../axios";
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import SearchIcon from '@mui/icons-material/Search';

// Styled Components
const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  transition: 'all 0.3s ease',
  boxShadow: theme.shadows[3],
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: theme.shadows[6],
  }
}));

const HeaderBox = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color: theme.palette.common.white,
  padding: theme.spacing(3),
  borderRadius: 16,
  marginBottom: theme.spacing(3),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const UserTask = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [itemsPerPage] = useState(6); // Number of items per page
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const response = await httpClient.get("/employee/getAll");
        setData(response.data);
        setFilteredData(response.data);
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (searchTerm === "") {
      setFilteredData(data);
      setPage(1); // Reset to first page when search is cleared
    } else {
      const filtered = data.filter(item =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredData(filtered);
      setPage(1); // Reset to first page when searching
    }
  }, [searchTerm, data]);

  // Get current items for pagination
  const indexOfLastItem = page * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleRedirect = (id) => {
    navigate(`/admin/task-list/${id}`);
  };

  return (
    <Container maxWidth="lg">
      <HeaderBox>
        <PersonOutlineOutlinedIcon sx={{ fontSize: 40, mr: 2 }} />
        <Typography variant="h4" component="h1">
          Employee Task Management
        </Typography>
      </HeaderBox>

      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search employees..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            sx: {
              borderRadius: 4,
              bgcolor: 'background.paper',
            }
          }}
        />
      </Box>

      <Grid container spacing={3}>
        {isLoading
          ? Array.from(new Array(6)).map((_, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Skeleton
                variant="rectangular"
                width="100%"
                height={120}
                sx={{ borderRadius: 4 }}
              />
            </Grid>
          ))
          : currentItems.length === 0
            ? (
              <Grid item xs={12}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" color="text.secondary">
                      {searchTerm ? "No matching employees found" : "No employees found"}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )
            : currentItems.map((item) => (
              <Grid item xs={12} md={6} key={item.id}>
                <StyledCard>
                  <CardContent>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar
                          sx={{
                            width: 56,
                            height: 56,
                            mr: 2,
                            bgcolor: 'primary.light'
                          }}
                        >
                          {item.name.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="h6">
                            {item.name}
                          </Typography>
                          <Chip
                            icon={<AssignmentOutlinedIcon />}
                            label="Employee"
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </Box>
                      </Box>
                      <IconButton
                        color="primary"
                        onClick={() => handleRedirect(item.id)}
                      >
                        <ArrowForwardIosIcon />
                      </IconButton>
                    </Box>
                  </CardContent>
                  <CardActions sx={{ justifyContent: 'flex-end', p: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => handleRedirect(item.id)}
                      endIcon={<AssignmentOutlinedIcon />}
                      sx={{
                        borderRadius: 4,
                        textTransform: 'none',
                      }}
                    >
                      View Tasks
                    </Button>
                  </CardActions>
                </StyledCard>
              </Grid>
            ))}
      </Grid>

      {!isLoading && filteredData.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
          <Stack spacing={2}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              size="large"
              showFirstButton
              showLastButton
              sx={{
                '& .MuiPaginationItem-root': {
                  borderRadius: 2,
                },
                '& .Mui-selected': {
                  fontWeight: 'bold',
                }
              }}
            />
          </Stack>
        </Box>
      )}
    </Container>
  );
};

export default UserTask;